// app/src/lib/cashflow/bankUpload.js — bank transaction CSV import.
//
// Imports a CSV bank export into the same `plaidTransactions` Dexie store the
// Plaid integration writes to. Each row gets a synthetic `plaidTxId` derived
// from a content hash (date + amount + normalized description), so re-uploading
// the same file is a no-op — the existing row is skipped rather than
// duplicated. Imported rows are tagged with `source: 'upload'` so consumers
// can distinguish them from Plaid-sourced rows if needed; all downstream
// classifiers (cashflow/taxes, cashflow/payroll, cashflow/rent, etc.)
// already treat every row identically.
//
// Dedup is CROSS-SOURCE: a transaction already on file from the Admin CSV
// import (`source:'csv'`, id `csv-…`), an earlier upload (`upload-…`), or —
// later — the Plaid feed must never be written twice. The synthetic id only
// collides with byte-identical re-uploads, so the importer ALSO matches on a
// normalized content key (date + signed amount + whitespace-collapsed,
// lower-cased description) against every existing row sharing the same date.
//
// Convention reconciliation:
//   - Most US bank CSVs export amounts as: NEGATIVE for debits (outflows),
//     POSITIVE for credits (inflows).
//   - Plaid / Kanjo convention: amountCents POSITIVE for outflows.
//   - We flip the sign during ingest. Re-imports stay deterministic
//     because the hash uses the flipped amount.

import { db } from '../db.js';

const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB
const UPLOAD_ITEM_ID = 'upload';
const UPLOAD_ACCOUNT_NAME = 'Uploaded transactions';

export class ImportError extends Error {
  constructor(message, code) { super(message); this.code = code; }
}

// ── Step 1: file-level validation ──────────────────────────────────────────
export function validateFile(file) {
  if (!file) return { ok: false, code: 'no_file', error: 'No file selected.' };
  if (file.size === 0) return { ok: false, code: 'empty', error: 'File is empty.' };
  if (!/\.csv$/i.test(file.name || '')) {
    return { ok: false, code: 'bad_ext', error: 'Only .csv files are supported. Please export your bank statement as CSV.' };
  }
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, code: 'too_large', error: 'File too large — please export a 12-month window or smaller from your bank.' };
  }
  return { ok: true };
}

// ── Step 2: CSV parse + column auto-detect ─────────────────────────────────
// Returns { ok, rows?: [{date, amountCents, description}], code?, error? }
//
// Rejects (per the v1 spec) when:
//   - file doesn't look like text (binary bytes in sample)
//   - no recognizable date / amount / description columns
//   - too few rows
export function parseCsv(text) {
  if (!text) return { ok: false, code: 'empty', error: 'File is empty.' };

  // Strip UTF-8 BOM (U+FEFF) if present. Using the escape form so the
  // invisible character isn't hidden in source.
  const cleaned = text.replace(/^﻿/, '');

  // Binary sniff: count control chars in the first 1KB. Real CSVs have ~zero
  // non-printable bytes outside of tab/CR/LF. A PDF or XLSX renamed to .csv
  // trips this immediately.
  const sample = cleaned.slice(0, 1024);
  let nonPrintable = 0;
  for (let i = 0; i < sample.length; i++) {
    const c = sample.charCodeAt(i);
    if (c < 32 && c !== 9 && c !== 10 && c !== 13) nonPrintable++;
  }
  if (nonPrintable > 16) {
    return { ok: false, code: 'not_csv', error: "This doesn't look like a CSV file. Please re-export as CSV from your bank." };
  }

  // Delimiter sniff on the first physical line — some banks export
  // tab-separated despite the .csv extension. A bank header never quotes a
  // field before its first delimiter, so peeking at the raw first line is safe.
  const headerLine = cleaned.split(/\r?\n/, 1)[0] || '';
  const commaCount = (headerLine.match(/,/g) || []).length;
  const tabCount = (headerLine.match(/\t/g) || []).length;
  const sep = tabCount > commaCount ? '\t' : ',';
  if (commaCount === 0 && tabCount === 0) {
    return { ok: false, code: 'no_columns', error: 'No columns detected. Please re-export as CSV from your bank.' };
  }

  // Quote/newline-aware tokenize. A quoted field may contain the delimiter,
  // a CRLF, or doubled "" quotes — Chase ACH/wire memos sometimes embed a
  // newline mid-description. The previous approach split on \n FIRST and then
  // parsed each line, which tore those rows apart and tripped the
  // column-consistency guard below — that's why a real Chase export failed
  // here while the admin importer (full tokenizer) accepted the same file.
  const parsed = tokenizeCsv(cleaned, sep).filter((r) => r.some((x) => String(x).trim() !== ''));
  if (parsed.length < 2) {
    return { ok: false, code: 'too_few_rows', error: 'CSV needs at least a header row and one transaction.' };
  }

  const expectedCols = parsed[0].length;
  // Reject if column counts vary wildly — common when the file isn't really
  // a CSV (e.g. log output, narrative export). Tolerate ±1 because some banks
  // omit the trailing empty field (e.g. Chase's blank "Check or Slip #") on
  // rows that don't use it.
  let inconsistent = 0;
  for (let i = 0; i < Math.min(parsed.length, 10); i++) {
    if (Math.abs(parsed[i].length - expectedCols) > 1) inconsistent++;
  }
  if (inconsistent > 3) {
    return { ok: false, code: 'inconsistent_cols', error: "Couldn't parse this file — columns don't line up. Please re-export as a standard CSV." };
  }

  const headers = parsed[0].map((h) => String(h || '').trim().toLowerCase());
  const sampleRows = parsed.slice(1, Math.min(11, parsed.length));
  const dateIdx = findDateColumn(headers, sampleRows);
  const descIdx = findDescriptionColumn(headers, sampleRows);
  const balanceIdx = findBalanceColumn(headers, sampleRows);
  // The transaction amount is EITHER a single signed "Amount" column OR a
  // separate Debit/Credit pair. Some banks export the latter, with BOTH columns
  // holding positive magnitudes (debit = outflow, credit = inflow). Prefer the
  // pair when both are present; otherwise fall back to a single signed column.
  const dc = findDebitCreditColumns(headers, sampleRows);
  const amountIdx = dc ? -1 : findAmountColumn(headers, sampleRows);

  if (dateIdx < 0 || descIdx < 0 || (!dc && amountIdx < 0)) {
    const missing = [];
    if (dateIdx < 0) missing.push('Date');
    if (!dc && amountIdx < 0) missing.push('Amount');
    if (descIdx < 0) missing.push('Description');
    return {
      ok: false,
      code: 'columns_unknown',
      error: `Couldn't recognize the columns (missing: ${missing.join(', ')}). Please re-export as a standard CSV with Date, Description, and Amount columns.`,
    };
  }

  const rows = [];
  for (let i = 1; i < parsed.length; i++) {
    const r = parsed[i];
    const date = parseDate(r[dateIdx]);
    const amountCents = dc ? debitCreditAmount(r, dc) : parseAmount(r[amountIdx]);
    const description = String(r[descIdx] || '').trim();
    if (!date || amountCents == null || !description) continue;
    // Running-balance column, when the export carries one. Drives the live cash
    // position; null when absent so the account balance is left untouched.
    const balanceCents = balanceIdx >= 0 ? parseAmount(r[balanceIdx]) : null;
    rows.push({ date, amountCents, description, balanceCents: balanceCents == null ? null : balanceCents });
  }
  if (rows.length === 0) {
    return { ok: false, code: 'no_rows', error: 'No valid transactions found in the CSV.' };
  }
  return { ok: true, rows };
}

// ── Helpers — tokenizer, column detectors, date/amount parsers ─────────────

// Multiline-aware tokenizer: returns an array of rows (each an array of cell
// strings), correctly handling quoted fields that contain the delimiter, an
// embedded newline, or a doubled "" quote. parseCsvLine (below) remains for
// single-line callers/tests, but parseCsv uses this so a quoted newline inside
// a description no longer breaks the row apart.
export function tokenizeCsv(text, sep) {
  const rows = [];
  let row = [], field = '', inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuote) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQuote = false; }
      else { field += c; }
    } else {
      if (c === '"') { inQuote = true; }
      else if (c === sep) { row.push(field); field = ''; }
      else if (c === '\n') { row.push(field); field = ''; rows.push(row); row = []; }
      else if (c === '\r') { /* swallow; the paired \n ends the row */ }
      else { field += c; }
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }  // flush unterminated last row
  return rows;
}

export function parseCsvLine(line, sep) {
  const cells = [];
  let cur = '', inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuote) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') { inQuote = false; }
      else { cur += c; }
    } else {
      if (c === '"') { inQuote = true; }
      else if (c === sep) { cells.push(cur); cur = ''; }
      else { cur += c; }
    }
  }
  cells.push(cur);
  return cells;
}

function findDateColumn(headers, sampleRows) {
  const hints = ['date', 'posted', 'posting'];
  for (let i = 0; i < headers.length; i++) {
    if (hints.some((h) => headers[i].includes(h))) {
      if (sampleRows.some((r) => parseDate(r[i]))) return i;
    }
  }
  // Heuristic: any column where ≥60% of sample rows look like a date.
  for (let i = 0; i < headers.length; i++) {
    const matches = sampleRows.filter((r) => parseDate(r[i])).length;
    if (sampleRows.length > 0 && matches >= sampleRows.length * 0.6) return i;
  }
  return -1;
}

function findAmountColumn(headers, sampleRows) {
  const hints = ['amount', 'debit', 'credit'];
  for (let i = 0; i < headers.length; i++) {
    // Skip running-balance columns even if they match 'amount' loosely.
    if (/balance/.test(headers[i])) continue;
    if (hints.some((h) => headers[i].includes(h))) {
      if (sampleRows.some((r) => parseAmount(r[i]) != null)) return i;
    }
  }
  // Fallback: numeric column with the highest signed-number density.
  let bestIdx = -1, bestScore = 0;
  for (let i = 0; i < headers.length; i++) {
    if (/balance/.test(headers[i])) continue;
    const matches = sampleRows.filter((r) => parseAmount(r[i]) != null).length;
    if (matches > bestScore && matches >= Math.max(1, sampleRows.length * 0.5)) {
      bestScore = matches; bestIdx = i;
    }
  }
  return bestIdx;
}

// Running-balance column ("Balance", "Running Balance"). Distinct from the
// amount column (findAmountColumn explicitly skips it). Drives the cash position.
function findBalanceColumn(headers, sampleRows) {
  for (let i = 0; i < headers.length; i++) {
    if (/balance/.test(headers[i]) && sampleRows.some((r) => parseAmount(r[i]) != null)) return i;
  }
  return -1;
}

// Separate Debit/Credit (or Withdrawal/Deposit) amount columns. Returns the
// distinct pair only when BOTH exist and at least one carries a number in the
// sample — so a lone "Type: DEBIT/CREDIT" indicator column (header 'type') or a
// single signed "Amount" column doesn't trip it. Each column holds a positive
// magnitude; debitCreditAmount() nets them into the bank's signed convention.
function findDebitCreditColumns(headers, sampleRows) {
  let debitIdx = -1, creditIdx = -1;
  for (let i = 0; i < headers.length; i++) {
    if (/balance/.test(headers[i])) continue;
    if (debitIdx < 0 && /\bdebit\b|withdrawal/.test(headers[i])) debitIdx = i;
    else if (creditIdx < 0 && /\bcredit\b|deposit/.test(headers[i])) creditIdx = i;
  }
  if (debitIdx >= 0 && creditIdx >= 0 && debitIdx !== creditIdx
      && sampleRows.some((r) => parseAmount(r[debitIdx]) != null || parseAmount(r[creditIdx]) != null)) {
    return { debitIdx, creditIdx };
  }
  return null;
}

// Net a Debit/Credit pair into the bank-CSV convention used here (+inflow /
// -outflow; rowsToTxs flips it to Plaid's +outflow). Magnitudes are taken as
// absolute so a stray sign in one column can't flip the entry. A row populates
// exactly one side; the empty side parses to null → treated as 0.
function debitCreditAmount(row, { debitIdx, creditIdx }) {
  const debit = parseAmount(row[debitIdx]);
  const credit = parseAmount(row[creditIdx]);
  if (debit == null && credit == null) return null;
  return Math.abs(credit || 0) - Math.abs(debit || 0);
}

function findDescriptionColumn(headers, sampleRows) {
  const hints = ['description', 'merchant', 'payee', 'memo', 'name', 'transaction'];
  for (let i = 0; i < headers.length; i++) {
    if (hints.some((h) => headers[i].includes(h))) return i;
  }
  // Fallback: longest-average-text column with average length ≥ 10 chars.
  let bestIdx = -1, bestLen = 0;
  for (let i = 0; i < headers.length; i++) {
    if (!sampleRows.length) break;
    const avg = sampleRows.reduce((s, r) => s + String(r[i] || '').length, 0) / sampleRows.length;
    if (avg > bestLen && avg > 10) { bestLen = avg; bestIdx = i; }
  }
  return bestIdx;
}

export function parseDate(s) {
  s = String(s || '').trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) {
    const y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
    if (validYmd(y, mo, d)) return iso(y, mo, d);
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
  if (m) {
    let y = parseInt(m[3], 10);
    if (y < 100) y = y < 50 ? 2000 + y : 1900 + y;
    const mo = parseInt(m[1], 10), d = parseInt(m[2], 10);
    if (validYmd(y, mo, d)) return iso(y, mo, d);
  }
  m = /^(\d{1,2})-(\d{1,2})-(\d{2,4})/.exec(s);
  if (m) {
    let y = parseInt(m[3], 10);
    if (y < 100) y = y < 50 ? 2000 + y : 1900 + y;
    const mo = parseInt(m[1], 10), d = parseInt(m[2], 10);
    if (validYmd(y, mo, d)) return iso(y, mo, d);
  }
  return null;
}
const validYmd = (y, mo, d) => y >= 2000 && y < 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31;
const iso = (y, mo, d) => `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export function parseAmount(s) {
  s = String(s || '').trim();
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[$,]/g, '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  let v = parseFloat(s);
  if (!Number.isFinite(v)) return null;
  if (neg) v = -v;
  return Math.round(v * 100);
}

// ── Step 3: persist to Dexie ───────────────────────────────────────────────
// Idempotent re-upload via content-hash plaidTxId. Skips rows already present.
export async function importCsv(file, userId) {
  if (!userId) throw new ImportError('Not signed in.', 'no_user');
  const v = validateFile(file);
  if (!v.ok) throw new ImportError(v.error, v.code);
  const text = await file.text();
  const parsed = parseCsv(text);
  if (!parsed.ok) throw new ImportError(parsed.error, parsed.code);

  await ensureUploadAccount(userId);
  // Refresh the "Synced …" timestamp the cash-flow report shows (asOf = max
  // lastSyncedAt across plaidItems). Without this, importing a CSV left the
  // header + staleness banner pinned to the last Plaid/Admin sync date even
  // though the user just imported this morning.
  await db.plaidItems.put({
    plaidItemId: UPLOAD_ITEM_ID, userId, status: 'HEALTHY',
    institutionName: UPLOAD_ACCOUNT_NAME, lastSyncedAt: Date.now(),
  });
  const txs = rowsToTxs(parsed.rows, userId);
  if (txs.length === 0) return { totalRows: 0, savedCount: 0, skippedCount: 0, fileName: file.name };

  // Dedup against EVERY transaction already on file (any source / id scheme),
  // not just prior uploads. A duplicate must share the date, so loading the
  // import's date span is sufficient. We skip a row when it matches an existing
  // row by synthetic id (identical re-upload) OR by normalized content key
  // (same transaction imported earlier under a different id scheme, e.g. 'csv').
  const lo = txs.reduce((m, t) => (t.date < m ? t.date : m), txs[0].date);
  const hi = txs.reduce((m, t) => (t.date > m ? t.date : m), txs[0].date);
  const existingRange = await db.plaidTransactions
    .where('[userId+date]').between([userId, lo], [userId, hi], true, true)
    .toArray();
  const existingIds = new Set(existingRange.map((r) => r.plaidTxId));
  const existingKeys = new Set(existingRange.map((r) => contentKey(r.date, r.amountCents, r.description || r.merchantName)));

  const batchKeys = new Set(); // collapse in-file dupes too
  const toWrite = [];
  let skippedCount = 0;
  for (const t of txs) {
    const key = contentKey(t.date, t.amountCents, t.description);
    if (existingIds.has(t.plaidTxId) || existingKeys.has(key) || batchKeys.has(key)) { skippedCount++; continue; }
    batchKeys.add(key);
    toWrite.push(t);
  }
  if (toWrite.length > 0) await db.plaidTransactions.bulkPut(toWrite);

  // Refresh the manual cash balance from the statement's running-balance column
  // (when present), so the cash position tracks the bank instead of sitting at a
  // stale snapshot. Uses ALL parsed rows (not just newly-written ones) so re-
  // importing a fresher statement still moves the balance even if every row was
  // already on file.
  const balInfo = currentBalanceFromRows(parsed.rows);
  if (balInfo) {
    try { await applyImportedBalance(userId, balInfo.balanceCents, balInfo.asOf); }
    catch (e) { console.warn('[bankUpload] balance update failed', e); }
  }

  return {
    totalRows: txs.length,
    savedCount: toWrite.length,
    skippedCount,
    fileName: file.name,
    balanceCents: balInfo ? balInfo.balanceCents : null,
  };
}

/**
 * The running balance AFTER the most-recent transaction in a parsed statement —
 * i.e. the account's current balance. Returns { balanceCents, asOf } or null
 * when the export carried no balance column.
 *
 * Statements come ordered either oldest→newest or newest→oldest; we detect the
 * direction from the first vs last row's date, then read the balance at the
 * NEWEST end. Respecting in-file order at that end resolves same-day rows to the
 * day's FINAL balance rather than a mid-day one (a plain date sort can't).
 * @param {Array<{date:string, balanceCents:number|null}>} rows
 */
export function currentBalanceFromRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (!rows.some((r) => r && r.balanceCents != null)) return null;
  const ascending = rows[0].date <= rows[rows.length - 1].date;
  if (ascending) {
    for (let i = rows.length - 1; i >= 0; i--) if (rows[i].balanceCents != null) return { balanceCents: rows[i].balanceCents, asOf: rows[i].date };
  } else {
    for (let i = 0; i < rows.length; i++) if (rows[i].balanceCents != null) return { balanceCents: rows[i].balanceCents, asOf: rows[i].date };
  }
  return null;
}

// Write the imported balance onto a SINGLE manual cash account so getPosition
// (which sums every depository account) can't double-count. Prefer the Admin
// CSV account when it already exists — updating it in place rather than minting
// a second balance-bearing account — otherwise this importer's own upload
// account. Guarded so an OLDER statement can't regress a newer balance.
async function applyImportedBalance(userId, balanceCents, asOf) {
  const admin = await db.plaidAccounts.get('csv-checking');
  const acctId = (admin && admin.userId === userId) ? 'csv-checking' : `upload-${userId}`;
  const acct = await db.plaidAccounts.get(acctId);
  if (!acct) return;
  if (acct.balanceAsOf && asOf < acct.balanceAsOf) return;
  await db.plaidAccounts.put({ ...acct, currentBalanceCents: balanceCents, balanceAsOf: asOf });
}

// Normalized content key for cross-source dedup: two rows are "the same
// transaction" when their date, signed amount, and description match —
// regardless of which importer wrote them. Collapses internal whitespace and
// lower-cases so the Admin 'csv' importer (raw memo) and this 'upload' path
// (also raw memo) — which may format whitespace slightly differently — still
// collide. Mirrors the fields synthHash keys on, but with the whitespace fold.
export function contentKey(date, amountCents, desc) {
  const norm = String(desc || '').toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 80);
  return `${date}|${amountCents}|${norm}`;
}

export function rowsToTxs(rows, userId) {
  const accountId = `upload-${userId}`;
  return rows.map((r) => {
    const amountCents = -r.amountCents; // bank-CSV convention → Plaid convention
    const plaidTxId = `upload-${synthHash(r.date, amountCents, r.description)}`;
    return {
      plaidTxId,
      userId,
      plaidAccountId: accountId,
      plaidItemId: UPLOAD_ITEM_ID,
      date: r.date,
      pending: 0,
      amountCents,
      // The running account balance AFTER this transaction, when the statement
      // carried a Balance column. Persisted (not indexed) so we can read the exact
      // bank balance as of any date — e.g. the 12/31 year-end balance that seeds
      // the prior-year reserve in the owner-draw report. Omitted when unknown.
      ...(r.balanceCents != null ? { balanceCents: r.balanceCents } : {}),
      merchantName: r.description,
      description: r.description,
      isoCurrencyCode: 'USD',
      source: 'upload',
    };
  });
}

function synthHash(date, amountCents, desc) {
  // Key on the FULL (normalized) description so two genuinely different rows that
  // happen to share a date + amount (e.g. CHECK 353 vs CHECK 354, or two separate
  // transfers with different transaction#s) stay distinct. Near-duplicates where
  // one form merely carries an extra confirmation number are collapsed later by
  // the admin "Remove duplicate transactions" cleanup, which uses a subset rule.
  const norm = `${date}|${amountCents}|${String(desc).trim().toLowerCase().slice(0, 80)}`;
  // FNV-1a 32-bit — stable across browsers, fast, fine for dedup keys.
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < norm.length; i++) {
    h ^= norm.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
    h >>>= 0;
  }
  return h.toString(16).padStart(8, '0');
}

async function ensureUploadAccount(userId) {
  const id = `upload-${userId}`;
  const existing = await db.plaidAccounts.get(id);
  if (existing) return;
  await db.plaidAccounts.put({
    plaidAccountId: id,
    userId,
    plaidItemId: UPLOAD_ITEM_ID,
    name: UPLOAD_ACCOUNT_NAME,
    type: 'depository',
    subtype: 'checking',
    currentBalanceCents: null,
    availableBalanceCents: null,
    isoCurrencyCode: 'USD',
  });
}

export const MAX_BYTES = MAX_FILE_BYTES;
